import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { HelpModeProvider } from "./contexts/HelpModeContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Toaster } from "sonner";

// Pages
import LoginPage, { AuthCallback } from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import PortfolioChangesPage from "./pages/PortfolioChangesPage";
import ImporterPage from "./pages/ImporterPage";
import PdfImporterPage from "./pages/PdfImporterPage";
import AssetsPage from "./pages/AssetsPage";
import AssetDetailPage from "./pages/AssetDetailPage";
import AssetLinkPage from "./pages/AssetLinkPage";
import EditAssetPage from "./pages/EditAssetPage";
import CreateAssetPage from "./pages/CreateAssetPage";
import ContactsPage from "./pages/ContactsPage";
import UsersPage from "./pages/UsersPage";
import InviteRegisterPage from "./pages/InviteRegisterPage";
import SettingsPage from "./pages/SettingsPage";
import PapeleraPage from "./pages/PapeleraPage";
import AuditoriaPage from "./pages/AuditoriaPage";
import ProfilePage from "./pages/ProfilePage";
import PendientesRevisionPage from "./pages/PendientesRevisionPage";
import ReviewDetailPage from "./pages/ReviewDetailPage";
import { PushNotificationsProvider } from "./components/PushNotifications";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import MapaPage from "./pages/MapaPage";
import GeographicZonesPage from "./pages/GeographicZonesPage";
import RecorridosPage from "./pages/RecorridosPage";
import RecorridoDetailPage from "./pages/RecorridoDetailPage";
import Layout from "./components/Layout";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user || user === false) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Inversor lockdown: only Base de Activos + Pendientes de revisión.
  if (user.role === 'inversor') {
    const path = location.pathname || '';
    const allowed = path.startsWith('/activos') || path === '/activos' || path.startsWith('/pendientes-revision');
    if (!allowed) {
      return <Navigate to="/activos" replace />;
    }
  }
  // Mediador lockdown: only operational read-only pages. NO dashboard.
  if (user.role === 'mediador') {
    const path = location.pathname || '';
    const allowed =
      path.startsWith('/activos') ||
      path.startsWith('/pendientes-revision') ||
      path === '/contactos' ||
      path === '/mapa' ||
      path.startsWith('/recorridos') ||
      path.startsWith('/perfil');
    if (!allowed) {
      return <Navigate to="/activos" replace />;
    }
  }

  // Superadmin-only pages: hard block at the route level so admin/gestor
  // never get a flash of the page while the API call returns 403.
  const SUPERADMIN_ONLY_PREFIXES = ['/usuarios', '/papelera', '/auditoria', '/parametros'];
  const requestedSettingsTab = new URLSearchParams(location.search).get('tab');
  const servicerAdminPage = user.role === 'admin' && (
    location.pathname === '/parametros/servicers' ||
    (location.pathname === '/parametros' && (!requestedSettingsTab || requestedSettingsTab === 'servicer'))
  );
  if (user.role !== 'superadmin' && !servicerAdminPage && SUPERADMIN_ONLY_PREFIXES.some(p => (location.pathname || '').startsWith(p))) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Mantener los enlaces existentes a importaciones y operaciones sin perder su identificador.
const LegacyServicersRedirect = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  query.set('tab', 'servicer');
  return <Navigate to={`/parametros?${query.toString()}`} replace />;
};

// App Router with session_id detection
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/a/:token" element={<AssetLinkPage />} />
      
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      
      <Route path="/importar" element={
        <ProtectedRoute>
          <ImporterPage />
        </ProtectedRoute>
      } />
      <Route path="/cambios-cartera" element={<ProtectedRoute><PortfolioChangesPage /></ProtectedRoute>} />

      <Route path="/importar-pdf" element={
        <ProtectedRoute>
          <PdfImporterPage />
        </ProtectedRoute>
      } />
      
      <Route path="/activos" element={
        <ProtectedRoute>
          <AssetsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/activos/:id" element={
        <ProtectedRoute>
          <AssetDetailPage />
        </ProtectedRoute>
      } />

      <Route path="/activos/:id/editar" element={
        <ProtectedRoute>
          <EditAssetPage />
        </ProtectedRoute>
      } />
      
      <Route path="/crear-activo" element={
        <ProtectedRoute>
          <CreateAssetPage />
        </ProtectedRoute>
      } />

      <Route path="/contactos" element={
        <ProtectedRoute>
          <ContactsPage />
        </ProtectedRoute>
      } />

      <Route path="/papelera" element={
        <ProtectedRoute>
          <PapeleraPage />
        </ProtectedRoute>
      } />

      <Route path="/auditoria" element={
        <ProtectedRoute>
          <AuditoriaPage />
        </ProtectedRoute>
      } />

      <Route path="/usuarios" element={
        <ProtectedRoute>
          <UsersPage />
        </ProtectedRoute>
      } />

      <Route path="/perfil" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />

      <Route path="/mapa" element={
        <ProtectedRoute>
          <MapaPage />
        </ProtectedRoute>
      } />
      <Route path="/zonas-exclusion" element={<ProtectedRoute><GeographicZonesPage /></ProtectedRoute>} />
      <Route path="/parametros/servicers" element={<ProtectedRoute><LegacyServicersRedirect /></ProtectedRoute>} />
      <Route path="/recorridos" element={
        <ProtectedRoute>
          <RecorridosPage />
        </ProtectedRoute>
      } />
      <Route path="/recorridos/:id" element={
        <ProtectedRoute>
          <RecorridoDetailPage />
        </ProtectedRoute>
      } />

      <Route path="/pendientes-revision/:reviewId" element={<ProtectedRoute><ReviewDetailPage /></ProtectedRoute>} />
      <Route path="/pendientes-revision" element={
        <ProtectedRoute>
          <PendientesRevisionPage />
        </ProtectedRoute>
      } />

      <Route path="/parametros" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />

      <Route path="/precios-base" element={<Navigate to="/parametros?tab=precios" replace />} />

      {/* Public invite registration - no auth required */}
      <Route path="/registro/:token" element={<InviteRegisterPage />} />

      {/* Public password reset - no auth required */}
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <PushNotificationsProvider>
            <HelpModeProvider>
              <Toaster position="top-right" richColors />
              <AppRouter />
            </HelpModeProvider>
            </PushNotificationsProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
